import React from 'react';
import styled from 'styled-components';
import { media } from '../theme/tokens';

interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  eyebrow?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
}

export const Drawer: React.FC<DrawerProps> = ({
  open,
  onClose,
  title,
  eyebrow,
  children,
  footer
}) => {
  return (
    <>
      <Overlay $open={open} onClick={onClose} />
      <Panel $open={open}>
        <Head>
          <Eyebrow>
            {eyebrow || 'Detail'}
            <CloseBtn onClick={onClose} aria-label="Close">
              ✕
            </CloseBtn>
          </Eyebrow>
          {title && <DrawerTitle>{title}</DrawerTitle>}
        </Head>
        <Body>{children}</Body>
        {footer && <Footer>{footer}</Footer>}
      </Panel>
    </>
  );
};

const Overlay = styled.div<{ $open: boolean }>`
  position: fixed;
  inset: 0;
  background: rgba(43, 61, 84, 0.4);
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  pointer-events: ${({ $open }) => ($open ? 'all' : 'none')};
  transition: opacity 0.25s;
  z-index: 90;
`;

const Panel = styled.div<{ $open: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 95vw;
  max-width: 520px;
  background: var(--white);
  border-left: 1px solid var(--line);
  box-shadow: var(--shadow-lg);
  transform: translateX(${({ $open }) => ($open ? '0' : '100%')});
  transition: transform 0.3s cubic-bezier(0.32, 0.72, 0, 1);
  z-index: 100;
  display: flex;
  flex-direction: column;
`;

const Head = styled.div`
  padding: 20px 20px 16px;
  border-bottom: 1px solid var(--line);
  flex-shrink: 0;

  ${media.md} {
    padding: 24px 28px 20px;
  }
`;

const Eyebrow = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: var(--ink-faint);
  margin-bottom: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  color: var(--ink-faint);
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 14px;

  &:hover {
    background: var(--paper);
    color: var(--ink);
  }
`;

const DrawerTitle = styled.h2`
  font-family: var(--font-sans-semibold);
  font-size: 22px;
  font-weight: 500;
  letter-spacing: -0.02em;
  line-height: 1.15;
  margin: 0;

  ${media.md} {
    font-size: 26px;
  }
`;

const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px;

  ${media.md} {
    padding: 24px 28px 40px;
  }

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: var(--line);
    border-radius: 3px;
  }
`;

const Footer = styled.div`
  padding: 16px 20px;
  border-top: 1px solid var(--line);
  background: var(--paper);
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  flex-shrink: 0;

  ${media.md} {
    padding: 16px 28px;
  }
`;
